<template>
  <div id="modal_channel_new" class="uk-modal modal_channel" uk-modal>
    <div class="uk-modal-dialog modal-channel-dialog">
      <button class="uk-modal-close-default" type="button" uk-close></button>
      <div class="uk-modal-header">
        <h2 class="uk-modal-title">
          <i :class="editingChannel ? 'uil-pen' : 'uil-plus-circle'" class="uil"></i>
          {{ editingChannel ? ($t("general.edit_channel")) : ($t("general.add_channel")) }}
        </h2>
      </div>
      <div class="uk-modal-body">
        <div class="form-group">
          <label for="new_channel_name">
            {{ $t("general.channel_name") }}
          </label>
          <input id="new_channel_name"
                 v-model="channelData.name"
                 class="form-control"
                 type="text"
          />
        </div>
        <div class="form-group mt-3 ml-3">
          <label class="form-label d-flex channel-private-toggle">
            <input v-model="channelData.is_private"
                   class="form-check-input mr-2 channel-private-checkbox"
                   type="checkbox"
            />
            {{ $t("general.private_channel") }}
          </label>
          <small class="form-text text-muted">
            {{ $t("general.private_channel_description") }}
          </small>
        </div>

        <div v-if="channelData.is_private" class="form-group mt-3 channel-members-section">
          <label for="channel_members">
            {{ $t("general.channel_members") }}
          </label>

          <div class="channel-criteria-box">
            <div class="channel-criteria-row">
              <div class="channel-criteria-multiselect">
                <multiselect v-model="memberCriterionTypeModel"
                             :allow-empty="false"
                             :maxHeight="200"
                             :openDirection="'bottom'"
                             :options="memberCriterionTypeOptions"
                             :placeholder="$t('general.select')"
                             :searchable="false"
                             deselectLabel=""
                             label="name"
                             selectLabel=""
                             selectedLabel=""
                             track-by="id"
                />
              </div>

              <div class="channel-criteria-multiselect">
                <multiselect v-model="memberCriterionValueModel"
                             :allow-empty="true"
                             :disabled="memberCriterionLoading"
                             :maxHeight="200"
                             :openDirection="'bottom'"
                             :options="memberCriterionValueOptions"
                             :placeholder="$t('general.select')"
                             :searchable="false"
                             deselectLabel=""
                             label="name"
                             selectLabel=""
                             selectedLabel=""
                             track-by="id"
                />
              </div>

              <button :disabled="!memberCriterionValue || criterionMatchedCount === 0"
                      class="btn btn-primary channel-criteria-btn"
                      type="button"
                      @click="handleApplyCriterion"
              >
                {{ $t("general.add") }}
              </button>
            </div>
            <small v-if="memberCriterionValue"
                   :class="{ 'channel-criteria-feedback--empty': criterionMatchedCount === 0 }"
                   class="channel-criteria-feedback"
            >
              {{
                criterionMatchedCount > 0
                    ? $t('general.channel_filter_users_found', { count: criterionMatchedCount })
                    : $t('general.channel_filter_no_users_found')
              }}
            </small>
          </div>

          <div class="channel-members-search">
            <input id="channel_members_search"
                   v-model="memberSearchQuery"
                   :placeholder="$t('general.search_users')"
                   class="form-control"
                   type="text"
                   @focus="showMemberDropdown = true"
            />
            <div v-if="showMemberDropdown && filteredUsers.length"
                 class="channel-members-dropdown"
            >
              <div v-for="user in filteredUsers"
                   :key="user.id"
                   :class="{ 'channel-member-option--selected': isMemberSelected(user) }"
                   class="channel-member-option"
                   @click="toggleMember(user)"
              >
                <UserProfileAvatar
                  :name="[user.name, user.surname].filter(Boolean).join(' ')"
                  :photo="user.photo_link"
                  class="channel-member-option-avatar"
                />
                <div class="channel-member-option-info">
                  <span class="channel-member-option-name">{{ user.name }} {{ user.surname }}</span>
                  <span class="channel-member-option-email">{{ user.email }}</span>
                </div>
                <i v-if="isMemberSelected(user)" class="uil uil-check channel-member-check"></i>
              </div>
            </div>
            <div v-else-if="showMemberDropdown && !filteredUsers.length"
                 class="channel-members-empty"
            >
              <div v-if="usersLoading" class="channel-loading-state">
                <span class="loader loader-primary"></span>
                <p>{{ $t("general.loading-users") }}</p>
              </div>
              <div v-else class="channel-empty-state">
                <i v-if="!memberSearchQuery" class="uil uil-users channel-empty-icon"></i>
                <i v-else class="uil uil-search channel-empty-icon"></i>
                <p>{{ memberSearchQuery ? 'Aranan kullanıcı bulunamadı' : 'Kullanıcı bulunamadı' }}</p>
              </div>
            </div>
          </div>

          <div v-if="channelData.selected_members && channelData.selected_members.length"
               class="channel-selected-members mt-2">
            <span v-for="member in channelData.selected_members"
                  :key="member.id"
                  class="channel-member-tag"
            >
              <UserProfileAvatar
                :name="[member.name, member.surname].filter(Boolean).join(' ')"
                :photo="member.photo_link"
                class="channel-member-tag-avatar"
              />
              <span class="channel-member-name">{{ member.name }} {{ member.surname }}</span>
              <button class="channel-member-remove"
                      type="button"
                      @click="removeMember(member)"
              >
                <i class="uil uil-times"></i>
              </button>
            </span>
          </div>
        </div>
      </div>
      <div class="uk-modal-footer uk-text-right">
        <button class="btn btn-outline-secondary uk-modal-close" type="button">
          {{ $t("general.cancel") }}
        </button>
        <button :disabled="isSaving" class="btn btn-primary" type="button" @click="handleSave">
          {{ $t("general.save") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import UserProfileAvatar from "@/view/components/common/UserProfileAvatar.vue";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

export default {
  name: "ChannelModal",
  components: {
    UserProfileAvatar,
    Multiselect,
  },
  props: {
    editingChannel: {
      type: Object,
      default: null,
    },
    users: {
      type: Array,
      default: () => [],
    },
    usersLoading: {
      type: Boolean,
      default: false,
    },
    memberCriterionTypeOptions: {
      type: Array,
      default: () => [],
    },
    memberCriterionValueOptions: {
      type: Array,
      default: () => [],
    },
    memberCriterionLoading: {
      type: Boolean,
      default: false,
    },
    criterionMatchedCount: {
      type: Number,
      default: 0,
    },
    isSaving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      channelData: {
        name: "",
        is_private: false,
        selected_members: [],
      },
      memberCriterionType: "position",
      memberCriterionValue: null,
      memberSearchQuery: "",
      showMemberDropdown: false,
    };
  },
  computed: {
    filteredUsers() {
      if (!this.users || !this.users.length) return [];
      if (!this.memberSearchQuery) return this.users;

      const query = this.memberSearchQuery.toLowerCase();
      return this.users.filter(user => {
        const fullName = `${user.name || ''} ${user.surname || ''}`.toLowerCase();
        const email = (user.email || '').toLowerCase();
        return fullName.includes(query) || email.includes(query);
      });
    },
    memberCriterionTypeModel: {
      get() {
        return (
            this.memberCriterionTypeOptions.find((item) => item.id === this.memberCriterionType) ||
            this.memberCriterionTypeOptions[0] ||
            null
        );
      },
      set(value) {
        this.memberCriterionType = value?.id || "position";
        this.$emit('criterion-type-change', this.memberCriterionType);
      },
    },
    memberCriterionValueModel: {
      get() {
        if (!this.memberCriterionValue) return null;
        return this.memberCriterionValueOptions.find(
            (item) => item.id === String(this.memberCriterionValue)
        ) || null;
      },
      set(value) {
        this.memberCriterionValue = value?.id ? String(value.id) : null;
        this.$emit('criterion-value-change', this.memberCriterionValue);
      },
    },
  },
  methods: {
    isMemberSelected(user) {
      if (!this.channelData.selected_members || !this.channelData.selected_members.length) return false;
      return this.channelData.selected_members.some((m) => String(m.id) === String(user.id));
    },
    toggleMember(user) {
      const index = this.channelData.selected_members.findIndex(
          (m) => String(m.id) === String(user.id)
      );
      if (index > -1) {
        this.channelData.selected_members.splice(index, 1);
      } else {
        this.channelData.selected_members.push(user);
      }
    },
    removeMember(member) {
      const index = this.channelData.selected_members.findIndex(
          (m) => String(m.id) === String(member.id)
      );
      if (index > -1) {
        this.channelData.selected_members.splice(index, 1);
      }
    },
    handleApplyCriterion() {
      this.$emit('apply-criterion', this.memberCriterionValue);
    },
    handleSave() {
      this.$emit('save', {
        name: this.channelData.name,
        is_private: this.channelData.is_private,
        selected_members: this.channelData.selected_members,
      });
    },
    handleOutsideClick(event) {
      const dropdown = event.target.closest('.channel-members-dropdown');
      const searchInput = event.target.closest('#channel_members_search');
      const memberTags = event.target.closest('.channel-member-tag');

      if (!dropdown && !searchInput && !memberTags) {
        this.showMemberDropdown = false;
      }
    },
    reset() {
      this.channelData.name = "";
      this.channelData.is_private = false;
      this.channelData.selected_members = [];
      this.memberCriterionType = "position";
      this.memberCriterionValue = null;
      this.memberSearchQuery = "";
      this.showMemberDropdown = false;
    },
    setChannelData(channel) {
      if (channel) {
        this.channelData.name = channel.name || channel.title || "";
        this.channelData.is_private = channel.is_private || channel.type === 2 || false;
        this.channelData.selected_members = channel.selected_members || [];
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
};
</script>

<style scoped>
/* Modal z-index */
.modal_channel {
  z-index: 999999 !important;
}

/* Modal Dialog */
.modal-channel-dialog {
  max-width: 680px;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.modal-channel-dialog .uk-modal-header {
  background: linear-gradient(180deg, #ffffff 0%, #f6f9ff 100%);
  border-bottom: 1px solid #e7edf6;
  padding: 18px 22px 16px;
}

.modal-channel-dialog .uk-modal-title {
  font-size: 26px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
}

.uk-modal-title .uil {
  font-size: 26px;
  color: var(--primaryColor, #666CFF);
}

.modal-channel-dialog .uk-modal-body {
  padding: 18px 22px 16px;
  background: #ffffff;
  overflow: visible;
}

.modal-channel-dialog .uk-modal-footer {
  background: #f8fbff;
  border-top: 1px solid #e7edf6;
  padding: 14px 22px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
}

/* Form Elements */
.form-group {
  margin-bottom: 0;
}

.modal-channel-dialog label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
  display: block;
}

.modal-channel-dialog .form-control {
  border: 1px solid #dbe3ee;
  border-radius: 10px;
  min-height: 48px;
  padding: 10px 12px;
  font-size: 16px;
  color: #1f2937;
  background: #fff;
  width: 100%;
  transition: all 0.2s ease;
}

.modal-channel-dialog .form-control:focus {
  outline: none;
  border-color: #0a66c2;
  box-shadow: 0 0 0 3px rgba(10, 102, 194, 0.12);
}

.form-control::placeholder {
  color: #adb5bd;
}

/* Private Channel Toggle */
.channel-private-toggle {
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
  padding: 10px 12px;
  border: 1px solid #e6edf6;
  border-radius: 10px;
  background: #f9fbff;
  display: flex;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  user-select: none;
}

.channel-private-checkbox {
  position: static !important;
  margin: 0 !important;
  transform: none !important;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: var(--primaryColor, #666CFF);
}

/* Members Section */
.channel-members-section {
  position: relative;
  border: 1px solid #e6edf6;
  border-radius: 14px;
  background: #f9fbff;
  padding: 10px;
  border-top: 1px solid #e9ecef;
  padding-top: 20px;
  margin-top: 20px;
}

.channel-members-section .form-label {
  margin-bottom: 0;
}

.channel-members-section .form-check-input {
  width: 44px;
  height: 24px;
  border-radius: 24px;
  border: none;
  background: #bbb;
  position: relative;
  cursor: pointer;
  transition: all 0.3s;
  appearance: none;
  flex-shrink: 0;
}

.channel-members-section .form-check-input:before {
  content: "";
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background: white;
  border-radius: 50%;
  transition: all 0.3s;
}

.channel-members-section .form-check-input:checked {
  background: #616061;
}

.channel-members-section .form-check-input:checked:before {
  transform: translateX(20px);
}

.channel-members-section .form-check-input:focus {
  box-shadow: none;
}

.channel-members-section .form-text {
  margin-left: 0;
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
}

/* Criteria Box */
.channel-criteria-box {
  position: relative;
  z-index: 20;
  background: #ffffff;
  border: 1px dashed #d7e2ef;
  border-radius: 12px;
  padding: 10px;
  margin-bottom: 10px !important;
}

.channel-criteria-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
}

.channel-criteria-multiselect {
  flex: 1;
  min-width: 140px;
}

.channel-criteria-multiselect ::v-deep .multiselect {
  min-height: 48px;
  font-size: 14px;
}

.channel-criteria-multiselect ::v-deep .multiselect__tags {
  min-height: 48px;
  border-radius: 12px;
  border: 1px solid #d5deeb;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  padding: 8px 36px 8px 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease;
}

.channel-criteria-multiselect ::v-deep .multiselect:hover .multiselect__tags {
  border-color: #bcc9dd;
}

.channel-criteria-multiselect ::v-deep .multiselect.multiselect--active .multiselect__tags {
  border-color: #5c7cfa;
  box-shadow: 0 0 0 3px rgba(92, 124, 250, 0.14);
  background: #ffffff;
}

.channel-criteria-multiselect ::v-deep .multiselect__single,
.channel-criteria-multiselect ::v-deep .multiselect__placeholder {
  font-size: 14px;
  color: #1f2937;
  line-height: 1.3;
}

.channel-criteria-multiselect ::v-deep .multiselect__single {
  font-weight: 600;
}

.channel-criteria-multiselect ::v-deep .multiselect__placeholder {
  color: #9aa4b2;
  font-weight: 500;
}

.channel-criteria-multiselect ::v-deep .multiselect__select {
  width: 34px;
  height: calc(100% - 2px);
  right: 1px;
}

.channel-criteria-multiselect ::v-deep .multiselect__select::before {
  border-color: #97a3b6 transparent transparent;
  border-width: 5px 5px 0;
}

.channel-criteria-multiselect ::v-deep .multiselect__content-wrapper {
  margin-top: 6px;
  border: 1px solid #d7e1ef;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: #ffffff;
  max-height: 150px !important;
  overflow-y: auto !important;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  z-index: 1800;
}

.channel-criteria-multiselect ::v-deep .multiselect__option {
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
}

.channel-criteria-multiselect ::v-deep .multiselect__option--highlight {
  background: #eef3ff !important;
  color: #1f2937 !important;
}

.channel-criteria-multiselect ::v-deep .multiselect__option--selected {
  background: #f3f6fc !important;
  color: #344054 !important;
}

.channel-criteria-multiselect ::v-deep .multiselect--disabled .multiselect__tags {
  background: #f7f9fc;
  border-color: #e6ebf3;
  color: #98a2b3;
}

.channel-criteria-btn {
  border-radius: 10px;
  font-weight: 600;
  min-width: 88px;
  height: 48px;
  padding: 0 18px;
}

.channel-criteria-btn:active {
  transform: scale(0.98);
  opacity: 0.9;
}

.channel-criteria-feedback {
  display: block;
  margin-top: 6px;
  padding: 0 2px;
  font-size: 12px;
  color: #0f4c81;
  font-weight: 500;
}

.channel-criteria-feedback--empty {
  color: #6b7280;
}

/* Members Search */
.channel-members-search {
  position: relative;
  z-index: 1;
  margin-top: 10px;
}

.channel-members-search .form-control {
  padding-left: 40px !important;
  min-height: 48px;
  border-radius: 10px;
}

.channel-members-search::before {
  content: "\f1b3";
  font-family: 'unicons';
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9a99a8;
  font-size: 18px;
  pointer-events: none;
}

.channel-members-search .channel-members-dropdown {
  position: relative;
  top: auto;
  margin-top: 6px;
  z-index: 1;
  width: 100%;
  max-height: min(320px, calc(100vh - 260px));
  overflow-y: auto !important;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  background: white;
  border: 1px solid #e3e3e3;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 0;
}

.channel-member-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  min-height: 56px;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid #f8f8f8;
}

.channel-member-option:last-child {
  border-bottom: none;
}

.channel-member-option:hover {
  background: #f8f8f8;
}

.channel-member-option--selected {
  background: #f8f8f8;
}

.channel-member-option-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.channel-member-option-avatar {
  width: 32px;
  height: 32px;
  min-width: 32px;
  font-size: 11px;
}

.channel-member-option-name {
  font-weight: 500;
  font-size: 15px;
  color: #1d1c1d;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.channel-member-option-email {
  font-size: 13px;
  color: #616061;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.channel-member-check {
  color: #007a5a;
  font-size: 18px;
  flex-shrink: 0;
}

/* Empty State */
.channel-members-empty {
  padding: 24px 16px;
  text-align: center;
  color: #616061;
  font-size: 14px;
}

.channel-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.channel-empty-icon {
  font-size: 36px;
  color: #9a99a8;
  margin: 0;
}

.channel-empty-state p {
  margin: 0;
  font-size: 13px;
  color: #616061;
}

.channel-loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 16px;
}

.channel-loading-state p {
  margin: 0;
  font-size: 13px;
  color: #616061;
}

/* Selected Members Tags */
.channel-selected-members {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  max-height: 210px;
  overflow-y: auto;
  padding-right: 2px;
}

.channel-member-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px 6px 6px;
  background: #ffffff;
  border: 1px solid #dbe3ee;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  transition: all 0.2s ease;
}

.channel-member-tag:hover {
  background: #f8f9fa;
  border-color: #c7d2e0;
}

.channel-member-tag-avatar {
  width: 24px;
  height: 24px;
  min-width: 24px;
  font-size: 9px;
}

.channel-member-name {
  margin: 0 4px;
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.channel-member-remove {
  background: none;
  border: none;
  color: #616061;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  margin-left: 4px;
  transition: all 0.2s ease;
}

.channel-member-remove:hover {
  background: #e5ecf5;
  color: #111827;
}

.channel-member-remove i {
  font-size: 12px;
  line-height: 1;
}

/* Buttons */
.modal-channel-dialog .btn {
  border-radius: 10px;
  font-weight: 600;
  min-width: 96px;
  min-height: 44px;
  padding: 10px 20px;
  font-size: 14px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-channel-dialog .btn.btn-primary {
  min-height: 48px;
  background: var(--primaryColor, #666CFF);
  color: #ffffff;
}

.btn-primary:hover:not(:disabled) {
  background: #5558e3;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 108, 255, 0.3);
}

.modal-channel-dialog .btn:active {
  transform: scale(0.98);
  opacity: 0.9;
}

.btn-primary:disabled,
.btn:disabled {
  opacity: 0.5;
  pointer-events: none;
  cursor: not-allowed;
}

.btn-outline-secondary {
  background: transparent;
  border: 1.5px solid #dee2e6;
  color: #6c757d;
}

.btn-outline-secondary:hover {
  background: #f8f9fa;
  border-color: #adb5bd;
}

/* Multiselect Override */
.multiselect {
  min-height: 40px;
  border-radius: 10px;
}

.multiselect__tags {
  border: 1.5px solid #dee2e6;
  border-radius: 10px;
  min-height: 40px;
  padding: 8px 40px 0 8px;
}

.multiselect__single {
  font-size: 14px;
  margin-bottom: 8px;
  padding-top: 0;
}

.multiselect__content-wrapper {
  border: 1.5px solid #dee2e6;
  border-radius: 10px;
  margin-top: 4px;
}

.multiselect__option--highlight {
  background: var(--primaryColor, #666CFF);
}

/* Loader */
.loader {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid var(--primaryColor, #666CFF);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 768px) {
  .channel-criteria-row {
    grid-template-columns: 1fr;
    align-items: stretch;
    flex-direction: column;
  }

  .channel-criteria-multiselect,
  .channel-criteria-btn {
    width: 100%;
  }

  .channel-criteria-multiselect {
    min-width: 0;
    max-width: 100%;
  }

  .modal-channel-dialog {
    max-width: calc(100vw - 20px);
    margin: 10px auto;
    border-radius: 14px;
  }

  .modal-channel-dialog .uk-modal-body {
    max-height: 70vh;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    padding-bottom: 120px;
  }

  .channel-criteria-multiselect,
  .modal-channel-dialog .form-control {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    font-size: 16px;
    box-sizing: border-box;
  }

  .modal-channel-dialog .btn {
    min-height: 48px;
  }

  .channel-criteria-box {
    overflow: visible;
  }

  .channel-selected-members {
    max-height: 180px;
  }

  .channel-member-name {
    max-width: 120px;
  }

  .uk-modal-header {
    padding: 20px;
  }

  .uk-modal-title {
    font-size: 18px;
  }
}

@media (hover: none) {
  .channel-criteria-multiselect ::v-deep .multiselect:hover .multiselect__tags {
    border-color: #d5deeb;
  }

  .channel-member-option:hover {
    transform: none;
    background: inherit;
    color: inherit;
    box-shadow: none;
  }
}
</style>
